import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import * as MoleculeUtil from 'chemistry/molecules/MoleculeUtil';
import {updateMoleculeVersion} from 'chemistry/molecules/MoleculeVersionReduxActions';
import {MoleculeFileQuery} from 'chemistry/molecules/tabs/__generated/MoleculeFileQuery';
import MoleculeChemicalStructureTabState from 'chemistry/molecules/tabs/MoleculeChemicalStructureTabState';
import MoleculePhysicochemicalPropertiesTabState from 'chemistry/molecules/tabs/MoleculePhysicochemicalPropertiesTabState';
import * as statusbar from 'components/StatusBar';
import {CHEMISTRY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import {FolderItemFileQueryVariables} from 'folder-items/ApolloFolderItemBase';
import ApolloRegistryItemBase from 'folder-items/ApolloRegistryItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {GetBioentityAnalyticsDashboardQuery} from 'insights/embeddings/data/__generated/GetBioentityAnalyticsDashboardQuery';
import GenerativeModelsTabState from 'model-hub/generative-models/GenerativeModelsTabState';
import initializeCoreBioentityWorkspaceTabs from 'nav/workspace/initializeCoreBioentityWorkspaceTabs';
import Preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type GraphQLMoleculeFileData = ExtractByTypename<MoleculeFileQuery['molecule'], 'MoleculeNode'>;

export const MOLECULE_FILE_QUERY: DocumentNode = gql`
  query MoleculeFileQuery($fileId: StrictString!) @engComponent(name: CHEMISTRY) {
    molecule(id: $fileId) @audit(msg: "Opened molecule {name}") {
      ... on MoleculeNode {
        name
        currentVersion {
          id
        }
        isContainedInComplexPolymer
        isContainedInComplexPolymerData {
          isContained
          errorMessage
        }
        ...BaseFolderItemFileGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class Molecule extends ApolloRegistryItemBase<MoleculeFileQuery, GraphQLMoleculeFileData> {
  protected getQueryWrapper(): QueryWrapper<MoleculeFileQuery, FolderItemFileQueryVariables> {
    return {query: MOLECULE_FILE_QUERY};
  }

  protected override queryVariables(): {fileId: string} {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: MoleculeFileQuery
  ): GraphQLMoleculeFileData | InaccessibleInfoGraphQL {
    return data.molecule;
  }

  protected getUpdatedQueryData(attrs: Partial<GraphQLMoleculeFileData>): MoleculeFileQuery {
    return {
      molecule: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  override setBanner(): void {
    if (!MoleculeUtil.onCurrentVersion(this.folderItem().id, this.folderItem().currentVersion.id)) {
      this.setBannerText('Editing disabled on previous versions.');
    } else if (this.isArchived()) {
      this.setBannerText('Editing disabled on archived molecule.');
    } else if (this.folderItem().isContainedInComplexPolymer) {
      this.setBannerText(
        `Editing chemical structure is disabled because molecule ${this.folderItem().isContainedInComplexPolymerData.errorMessage!}`
      );
    } else if (!this.allowChemicalStructureEdits()) {
      this.setBannerText('Editing chemical structure is disabled due to insufficient permissions.');
    } else {
      this.setBannerText(null);
    }
  }

  allowChemicalStructureEdits(): boolean {
    // TODO (BNCH-80624): This is a permissions bug - we should be checking the chemical structure edit
    // permission instead of the more generic write permission. Technically, this means that users without
    // write permission but with chemical structure edit permission would be incorrectly blocked from editing
    // the chemical structure of a molecule.
    return this.writable() && !this.folderItem().isContainedInComplexPolymer;
  }

  override allowEditsToNote(): boolean {
    return (
      this.writable() &&
      MoleculeUtil.onCurrentVersion(this.folderItem().id, this.folderItem().currentVersion.id)
    );
  }

  duplicate(): void {
    FolderSelectorModalClass.openForCopy(
      {
        showInventory: true,
      },
      (folderId) => {
        FileBrowserActions.copyAndOpenItem(this.id, folderId);
      }
    );
  }
}

export class MoleculeHistoryPanelState extends Backbone.Model {}

class MoleculeInformationPanelState extends Backbone.Model {}

interface MoleculeToolbarStateAttrs {
  moleculeHistoryPanelState: MoleculeHistoryPanelState;
  moleculeInformationPanelState: MoleculeInformationPanelState;
}

export class MoleculeToolbarState extends Backbone.Model {
  override defaults(): MoleculeToolbarStateAttrs {
    return {
      moleculeHistoryPanelState: new MoleculeHistoryPanelState(),
      moleculeInformationPanelState: new MoleculeInformationPanelState(),
    };
  }
}

export default class MoleculeFileState extends FolderItemState {
  static override Model: typeof Molecule;

  static override initClass(): void {
    this.fileType = ModelTypeConstants.MOLECULE;
    this.Model = Molecule;
  }

  override hasMetadata(): true {
    // Override BenchlingConfig.ENABLE_REGISTRY so BFI share links are usable
    return true;
  }

  override defaults(): FileStateAttributes {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(super.defaults(), {
      toolbar: new MoleculeToolbarState(),
      statusbar: new statusbar.MoleculeStatusBarState(),
    });
  }

  override initialize(
    attributes: {bioentityDashboardData?: GetBioentityAnalyticsDashboardQuery},
    options: unknown
  ): void {
    super.initialize(attributes, options);
    initializeCoreBioentityWorkspaceTabs(
      this.buffers(),
      this.metadataTab,
      this.entityMapTab,
      this.noteTab,
      this.resultsTab,
      this.storageTab,
      this.childEntityTabs,
      this.embeddedDashboardTab,
      this.relevantItemsTab,
      this.testOrdersTab
    );
    // Only render molecule data if chemistry product is enabled
    if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      const chemicalStructureTab = new MoleculeChemicalStructureTabState({
        moleculeId: this.model().id,
        molecule: this.get('molecule'),
      });
      this.buffers().at(0).tabs().add(chemicalStructureTab);
      chemicalStructureTab.set({active: true});
      if (!BenchlingConfig.CHEMISTRY_HIDE_PHYSICOCHEMICAL_PROPERTIES_TAB || !this.model().isRegistered()) {
        const physicochemicalPropertiesTab = new MoleculePhysicochemicalPropertiesTabState({
          moleculeId: this.model().id,
        });
        this.buffers().at(0).tabs().add(physicochemicalPropertiesTab);
      }
      if (BenchlingConfig.MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB) {
        this.buffers().addTab(
          new GenerativeModelsTabState({
            currentEntityType: ModelTypeConstants.MOLECULE,
            currentEntityId: this.model().id,
          }),
          {side: 'right'}
        );
      }
      analytics.trackEvent(CHEMISTRY_ANALYTICS_ACTIONS.VIEWED_MOLECULE, {
        moleculeId: this.get('id'),
        moleculeVersionId: this.get('molecule').folderItem().currentVersion.id,
      });
    }

    getStore().dispatch(
      updateMoleculeVersion(this.get('id'), this.get('molecule').folderItem().currentVersion.id)
    );
    const reduxUnsubscribe = getStore().subscribe(this.get('molecule').setBanner.bind(this.get('molecule')));
    this.set({reduxUnsubscribe});
    // Apply split workspace preference
    const splitPref = Preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : true);
  }

  override isRegistryItem(): true {
    return true;
  }

  override cleanUp(): void {
    super.cleanUp();
    this.get('reduxUnsubscribe')();
  }
}
MoleculeFileState.initClass();
